import { Box, Container, Pagination, Stack, Typography, CircularProgress } from "@mui/material";
import { useState, useEffect } from "react";
import { useListCars } from "../../features/car/car-exists/api/useListCar";
import { ListCarsSearchComponent } from "../../features/car/car-exists/ui/list-cars-search.component";
import { CarsListComponent } from "../../features/car/car-exists/ui/car-list.component";
import _ from "lodash";

interface ListCarsPageProps {
  term: string;
  startDate: Date;
  endDate: Date;
  page: number;
  limit: number;
  shouldListByDate: boolean;
  enabled: boolean;
}

const YESTERDAY = new Date(new Date().getTime() - 24 * 60 * 60 * 1000);

export const ListCarsPage = () => {
  const [pageProps, setPageProps] = useState<ListCarsPageProps>({
    term: "",
    startDate: YESTERDAY,
    endDate: new Date(),
    page: 1,
    limit: 20,
    shouldListByDate: false,
    enabled: true
  });
  const [termMask, setTermMask] = useState<string>("");

  const { data, error, isLoading, isFetching } = useListCars(pageProps);
  const { page, limit } = pageProps;

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setPageProps({ ...pageProps, page: value });
  };

  const debouncedSetPageProps = _.debounce(() => setPageProps({ ...pageProps, term: termMask, page: 1 }), 500);

  useEffect(() => {
    debouncedSetPageProps();
    return () => {
      debouncedSetPageProps.cancel();
    };
  }, [termMask]);

  // Only show full page loading on the very first load (when user hasn't typed anything yet)
  if (isLoading && !data && termMask === "") {
    return (
      <Container component="main" maxWidth="lg">
        <Box
          sx={{
            marginTop: 8,
            minHeight: "72vh",
            display: "flex",
            justifyContent: "center",
            alignItems: "center"
          }}
        >
          <CircularProgress size={60} />
        </Box>
      </Container>
    );
  }

  return (
    <Container component="main" maxWidth="lg">
      <Box
        sx={{
          marginTop: 8,
          display: "flex",
          flexDirection: "column",
          minHeight: "72vh" // Same as costumers page
        }}
      >
        {/* Title */}
        <Typography component="h1" variant="h4" sx={{ mb: 4 }}>
          Lista de Carros
        </Typography>

        {/* Search Box */}
        <Box sx={{ mb: 4 }}>
          <ListCarsSearchComponent
            props={{
              termMask,
              handleTermMaskChange: (term: string) => setTermMask(term)
            }}
          />
        </Box>

        {/* Results Area - Flex grow to fill remaining space */}
        <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
          {isFetching ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                flex: 1,
                minHeight: "300px"
              }}
            >
              <CircularProgress size={60} />
              <Typography variant="h6" sx={{ mt: 2 }}>
                Buscando carros...
              </Typography>
            </Box>
          ) : (
            <>
              {/* Car List */}
              {data && data.cars && data.cars.length > 0 && (
                <>
                  <Box sx={{ flex: 1 }}>
                    <CarsListComponent
                      props={{
                        cars: data.cars
                      }}
                    />
                  </Box>

                  <Stack spacing={2} sx={{ mt: 4, alignItems: "center" }}>
                    <Pagination count={Math.ceil(data.total / limit)} size="large" onChange={handlePageChange} page={page} />
                  </Stack>
                </>
              )}

              {/* No results */}
              {data && data.cars.length === 0 && termMask && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    flex: 1,
                    minHeight: "300px"
                  }}
                >
                  <Typography variant="h6" color="text.secondary">
                    Nenhum carro encontrado para "{termMask}"
                  </Typography>
                </Box>
              )}

              {/* Initial state */}
              {(!termMask || termMask === "") && (!data || data.cars.length === 0) && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    flex: 1,
                    minHeight: "300px"
                  }}
                >
                  <Typography variant="h6" color="text.secondary">
                    Digite uma placa, chassi ou cliente para buscar carros
                  </Typography>
                </Box>
              )}
            </>
          )}
        </Box>
      </Box>
    </Container>
  );
};

export default ListCarsPage;
